import { Card, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { AggregateSectionData } from 'scenes/Kit/Dashboard/types';

import { User } from 'models/User';

import { getComplianceColor } from '../utils';

const BASE =
  'scenes.kit.dashboard_alternative_mode.components.dashboard_kpi_cards';

const { Text } = Typography;

interface DashboardKpiCardsProps {
  data?: AggregateSectionData;
  loading?: boolean;
  user: User;
}

interface KpiCardConfig {
  title: string;
  value: string | number;
  valueColor?: string;
}

function getFeaturedCityLabel(countryCode: string): string {
  switch (countryCode) {
    case 'PE':
      return 'Lima';
    case 'CL':
      return 'Santiago';
    case 'CO':
      return 'Bogotá';
    default:
      return 'Especiales';
  }
}

function KpiCard({ title, value, valueColor }: KpiCardConfig) {
  return (
    <Card className="kit-dashboard-alt__kpi-card" bordered={false}>
      <Text type="secondary" className="kit-dashboard-alt__kpi-label">
        {title}
      </Text>
      <div
        className="kit-dashboard-alt__kpi-value"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </div>
    </Card>
  );
}

function DashboardKpiCards({ data, loading, user }: DashboardKpiCardsProps) {
  const { t } = useTranslation();
  const dash = '—';

  const cityLabel = getFeaturedCityLabel(user.company.countryCode);

  const cards: KpiCardConfig[] = [
    {
      title: t(`${BASE}.labels.required_shifts`),
      value: data ? data.totalShifts : dash,
    },
    {
      title: t(`${BASE}.labels.executed_shifts`),
      value: data ? data.totalExecutedShifts : dash,
    },
    {
      title: t(`${BASE}.labels.compliance`),
      value: data ? `${data.compliancePct.toFixed(2)}%` : dash,
      valueColor: data ? getComplianceColor(data.compliancePct) : undefined,
    },
    {
      title: t(`${BASE}.labels.featured_shifts`, { city: cityLabel }),
      value: data ? data.totalFeaturedShifts : dash,
    },
    {
      title: t(`${BASE}.labels.region_shifts`),
      value: data ? data.totalNonFeaturedShifts : dash,
    },
  ];

  if (loading) {
    return (
      <div className="kit-dashboard-alt__kpi-row">
        {Array(5)
          .fill(null)
          .map((_, idx) => (
            <div key={idx} className="kit-dashboard-alt__kpi-item">
              <Card className="kit-dashboard-alt__kpi-card" bordered={false}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <Skeleton.Input active style={{ width: '70%', height: 16 }} />
                  <Skeleton.Input active style={{ width: '40%', height: 24 }} />
                </div>
              </Card>
            </div>
          ))}
      </div>
    );
  }

  return (
    <div className="kit-dashboard-alt__kpi-row">
      {cards.map((card) => (
        <div key={card.title} className="kit-dashboard-alt__kpi-item">
          <KpiCard {...card} />
        </div>
      ))}
    </div>
  );
}

export default DashboardKpiCards;
